import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useReactToPrint } from "react-to-print";
import Button from "react-bootstrap/Button";
import { FaEye } from "react-icons/fa";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import { TbReportSearch } from "react-icons/tb";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { SHORT_DAYS_OF_WEEK, SHORT_MONTHS } from "@/utils/enums/DateEnum";
import { User } from "@/@types/sintegrada/User";
import { getProfissionalMedicalAttendance } from "@/services/sintegrada/professional/getProfissionalMedicalAttendance";
import { timeToString } from "@/utils/dateUtil";
import {
	getNonSpecializedMedicalAttendanceDashboard,
	NonSpecializedMedicalAttendanceDashboardData,
} from "@/services/sintegrada/attendance/getNonSpecializedMedicalAttendanceDashboard";
import { MedicalAttendanceTypeEnum } from "@/utils/enums/MedicalAttendanceTypeEnum";
import { calculatePercentage } from "@/utils/calculatePercentage";
import { defaultMedicalTypeOption, MedicalTypeOption } from "@/components/MedicalTypeSelect";

import { MedicalOfficeDashboardFilters } from "./MedicalOfficeDashboardFilters";
import { BarChart } from "@/components/Charts/BarChart";
import { DonutChart } from "@/components/Charts/DonutChart";
import { MedicalOfficeDashboardCards } from "./MedicalOfficeDashboardCards";
import { MedicalOfficeCards } from "./MedicalOfficeCards";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PrintComponent } from "@/components/PrintComponent";
import { AttendanceByProfessionalModal } from "./AttendanceByProfessionalModal";
import { AttendanceByProfessionalTable } from "./AttendanceByProfessionalTable";

dayjs.extend(utc);
dayjs.extend(timezone);

export function MedicalOfficeDashboard() {
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const { setTitle } = useLayout();
	const [startDate, setStartDate] = useState(
		dayjs().add(-1, "month").startOf("day").format("YYYY-MM-DDTHH:mm")
	);
	const [endDate, setEndDate] = useState(dayjs().endOf("day").format("YYYY-MM-DDTHH:mm"));
	const [minAge, setMinAge] = useState<number>();
	const [maxAge, setMaxAge] = useState<number>();
	const [professionalId, setProfessionalId] = useState<number>();
	const [medicalType, setMedicalType] = useState<MedicalTypeOption>(defaultMedicalTypeOption);
	const [professionals, setProfessionals] = useState<User[]>([]);
	const [dashboard, setDashboard] = useState<NonSpecializedMedicalAttendanceDashboardData>();
	const [isLoading, setIsLoading] = useState(false);
	const printPageRef = useRef<HTMLDivElement | null>(null);
	const [showModal, setShowModal] = useState(false);

	const printPage = useReactToPrint({
		content: () => printPageRef.current,
	});

	const dashboardData = dashboard || {
		totalMedicalAttendancesRequested: 0,
		totalMedicalAttendancesEvaluated: 0,
		aggregatedByPeriodAndMedicalAttendanceType: [],
		aggregatedByRiskClassification: [],
		aggregatedByDayOfWeek: [],
		aggregatedByGender: [],
		averageTimeInSecondsByRiskClassification: [],
		averageWaitTimeInSecondsByRiskClassification: [],
		aggregatedByProfessionalsAndMedicalAttendanceType: [],
		averageTimeInSecondsByProfessional: [],
		evasions: 0,
		aggregatedByHourOfDay: [],
		averageMedicalAttendancesPerDay: 0,
		averageTime: 0,
		minTime: 0,
		maxTime: 0,
		averageWaitTime: 0,
		minWaitTime: 0,
		maxWaitTime: 0,
		totalMedicalAttendancesOnTime: 0,
		totalMedicalAttendancesOutTime: 0,
		totalMedicalAttendancesWithoutRiskClassification: 0,
		aggregatedByShift: [],
	};

	async function fetchDashboard() {
		setIsLoading(true);
		setDashboard(
			await getNonSpecializedMedicalAttendanceDashboard({
				uf,
				ibgeCode,
				cnes,
				startDate: dayjs(startDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.startOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				endDate: dayjs(endDate)
					.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
					.endOf("minute")
					.format("YYYY-MM-DDTHH:mm:ssZ"),
				minAge,
				maxAge,
				professionalId,
				medicalType: medicalType?.value?.medicalType,
				specialty_id: medicalType?.value?.specialtyId,
			})
		);
		setIsLoading(false);
	}

	async function fetchProfessional() {
		setProfessionals(await getProfissionalMedicalAttendance({ uf, ibgeCode, cnes }));
	}

	useEffect(() => {
		setTitle("Monitoramento - Consultório médico");
		fetchDashboard();
		fetchProfessional();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="mx-3">
			<LoadingScreen loading={isLoading} />
			<Row className="m-2">
				<Card className="h-100 shadow-sm">
					<Card.Body>
						<MedicalOfficeDashboardFilters
							startDate={startDate}
							setStartDate={setStartDate}
							endDate={endDate}
							setEndDate={setEndDate}
							setMinAge={setMinAge}
							setMaxAge={setMaxAge}
							setProfessionalId={setProfessionalId}
							medicalType={medicalType}
							setMedicalType={setMedicalType}
							professionals={professionals}
							handleFilter={fetchDashboard}
							handlePrint={printPage}
						/>
					</Card.Body>
				</Card>
			</Row>
			<Row className="m-2">
				<Card className="bg-white shadow-sm">
					<Card.Header className="p-0">
						<Card.Title className="d-flex align-items-center gap-2 p-2 text-primary mb-0">
							<TbReportSearch
								size={30}
								className="bg-primary text-white rounded p-1 me-2"
							/>
							Consultório médico
						</Card.Title>
					</Card.Header>
				</Card>
			</Row>
			<PrintComponent
				printComponentRef={printPageRef}
				headerContainer={
					<div>
						<p className="text-center fs-3">Monitoramento - Consultório médico</p>
						{startDate && endDate && (
							<div>
								<p className="text-center">
									<span>{`${dayjs(startDate).format("DD/MM/YYYY")} Á ${dayjs(
										endDate
									).format("DD/MM/YYYY")}`}</span>
								</p>
							</div>
						)}
						{minAge && maxAge && (
							<p>
								<strong>Faixa etária:</strong>
								<span>{`${minAge} anos á ${maxAge} anos`}</span>
							</p>
						)}
						{professionalId && (
							<p>
								<strong>Profissional:</strong>
								<span>
									{professionals.find((p) => p.id === professionalId)?.name}
								</span>
							</p>
						)}
					</div>
				}
			>
				<Row className="ms-3">
					<MedicalOfficeDashboardCards
						totalAttendances={
							dashboardData.totalMedicalAttendancesRequested +
							dashboardData.totalMedicalAttendancesEvaluated
						}
						totalMedicalAttendancesRequested={
							dashboardData.totalMedicalAttendancesRequested
						}
						totalMedicalAttendancesEvaluated={
							dashboardData.totalMedicalAttendancesEvaluated
						}
						evasions={dashboardData.evasions}
						averageWaitTime={dashboardData.averageWaitTime}
						minWaitTime={dashboardData.minWaitTime}
						maxWaitTime={dashboardData.maxWaitTime}
						averageTime={dashboardData.averageTime}
						minTime={dashboardData.minTime}
						maxTime={dashboardData.maxTime}
						averageMedicalAttendancesPerDay={
							dashboardData.averageMedicalAttendancesPerDay
						}
						averageWaitTimeInSecondsByRiskClassification={
							dashboardData.averageWaitTimeInSecondsByRiskClassification
						}
					/>
				</Row>
				<Row className="mt-2 page-break-before">
					<Col sm={12} lg={6} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Volume de atendimentos médicos por gênero</Card.Title>
							</Card.Header>
							<Card.Body className="d-flex justify-content-center align-items-center">
								<DonutChart
									labels={dashboardData.aggregatedByGender.map((aggregated) =>
										aggregated.key === "M"
											? "Masculino"
											: aggregated.key === "F"
											? "Feminino"
											: "Outros"
									)}
									series={dashboardData.aggregatedByGender.map(
										(aggregated) => aggregated.total
									)}
								/>
							</Card.Body>
						</Card>
					</Col>
					<Col sm={12} lg={6} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Atendimentos por turno</Card.Title>
							</Card.Header>
							<Card.Body className="d-flex justify-content-center align-items-center">
								<DonutChart
									labels={dashboardData.aggregatedByShift.map(
										(aggregated) => aggregated.key
									)}
									series={dashboardData.aggregatedByShift.map(
										(aggregated) => aggregated.total
									)}
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<Row className="p-2 mt-2 page-break-before">
					<Card className="h-100 shadow-sm">
						<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
							<Card.Title>Atendimento médico x Reavaliação</Card.Title>
						</Card.Header>
						<Card.Body>
							<BarChart
								categories={dashboardData.aggregatedByPeriodAndMedicalAttendanceType.map(
									(aggregated) =>
										`${aggregated.key.year}/${
											SHORT_MONTHS[aggregated.key.month - 1]
										}`
								)}
								series={[
									{
										name: "Atendimento médico",
										data: dashboardData.aggregatedByPeriodAndMedicalAttendanceType.map(
											(aggregated) =>
												(aggregated.values || []).find(
													(medicalAttendanceTypeGroup) =>
														medicalAttendanceTypeGroup.key ===
														MedicalAttendanceTypeEnum.REQUESTED
												)?.total || 0
										),
									},
									{
										name: "Reavaliação",
										data: dashboardData.aggregatedByPeriodAndMedicalAttendanceType.map(
											(aggregated) =>
												(aggregated.values || []).find(
													(medicalAttendanceTypeGroup) =>
														medicalAttendanceTypeGroup.key ===
														MedicalAttendanceTypeEnum.EVALUATED
												)?.total || 0
										),
									},
								]}
								minHeight={400}
								categoryTextWidth={400}
								baseMultiplier={10}
								isVertical
							/>
						</Card.Body>
					</Card>
				</Row>
				<Row className="p-2">
					<Col sm={12} className="mb-3">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex justify-content-between fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Métricas de Tempo - Consultório Médico</Card.Title>
							</Card.Header>
							<Card.Body>
								<Row>
									<Col md={6} className="mb-3">
										<div className="text-center">
											<h5 className="text-muted mb-2">
												Tempo de Atendimento
											</h5>
											<div className="row">
												<Col xs={4} className="text-center">
													<div className="border-end">
														<div
															className="fw-bold text-primary"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.averageTime
															)}
														</div>
														<small className="text-muted">Médio</small>
													</div>
												</Col>
												<Col xs={4} className="text-center">
													<div className="border-end">
														<div
															className="fw-bold text-success"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(dashboardData.minTime)}
														</div>
														<small className="text-muted">Mínimo</small>
													</div>
												</Col>
												<Col xs={4} className="text-center">
													<div>
														<div
															className="fw-bold text-danger"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(dashboardData.maxTime)}
														</div>
														<small className="text-muted">Máximo</small>
													</div>
												</Col>
											</div>
										</div>
									</Col>
									<Col md={6} className="mb-3">
										<div className="text-center">
											<h5 className="text-muted mb-2">Tempo de Espera</h5>
											<div className="row">
												<Col xs={4} className="text-center">
													<div className="border-end">
														<div
															className="fw-bold text-primary"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.averageWaitTime
															)}
														</div>
														<small className="text-muted">Médio</small>
													</div>
												</Col>
												<Col xs={4} className="text-center">
													<div className="border-end">
														<div
															className="fw-bold text-success"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.minWaitTime
															)}
														</div>
														<small className="text-muted">Mínimo</small>
													</div>
												</Col>
												<Col xs={4} className="text-center">
													<div>
														<div
															className="fw-bold text-danger"
															style={{ fontSize: "1.5rem" }}
														>
															{timeToString(
																dashboardData.maxWaitTime
															)}
														</div>
														<small className="text-muted">Máximo</small>
													</div>
												</Col>
											</div>
										</div>
									</Col>
								</Row>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<Row className="p-2">
					<Col sm={12} className="mb-3">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex justify-content-between fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>
									Tempo de Espera por Classificação de Risco - Consultório Médico
								</Card.Title>
							</Card.Header>
							<Card.Body>
								<Row className="justify-content-center">
									{dashboardData.averageWaitTimeInSecondsByRiskClassification?.map(
										(item, index) =>
											item.key && (
												<Col
													key={index}
													sm={6}
													md={3}
													lg={2}
													className="mb-3"
												>
													<div className="text-center">
														<div
															className="rounded mx-auto mb-2 d-flex align-items-center justify-content-center"
															style={{
																width: "80px",
																height: "40px",
																backgroundColor: item.key.color,
																color: "white",
																fontWeight: "bold",
																fontSize: "0.9rem",
															}}
														>
															{item.key.name}
														</div>
														<div className="mb-1">
															<small className="text-muted">
																Médio:{" "}
															</small>
															<span
																className="fw-bold text-dark"
																style={{ fontSize: "1rem" }}
															>
																{timeToString(item.total)}
															</span>
														</div>
														<div>
															<small className="text-muted">
																Máximo:{" "}
															</small>
															<span
																className="text-muted"
																style={{ fontSize: "0.9rem" }}
															>
																{timeToString(
																	item.key.attendanceTimeMinutes *
																		60
																)}
															</span>
														</div>
														<div className="mt-1">
															{item.total >
															item.key.attendanceTimeMinutes * 60 ? (
																<span className="badge bg-danger">
																	Acima
																</span>
															) : (
																<span className="badge bg-success">
																	Dentro
																</span>
															)}
														</div>
													</div>
												</Col>
											)
									)}
								</Row>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<Row className="ms-3 mt-2 page-break-before">
					<MedicalOfficeCards
						totalMedicalAttendancesOnTime={dashboardData.totalMedicalAttendancesOnTime}
						totalMedicalAttendancesOutTime={
							dashboardData.totalMedicalAttendancesOutTime
						}
						totalMedicalAttendancesWithoutRiskClassification={
							dashboardData.totalMedicalAttendancesWithoutRiskClassification
						}
						percentageEvasions={calculatePercentage(
							dashboardData.evasions,
							dashboardData.totalMedicalAttendancesEvaluated +
								dashboardData.totalMedicalAttendancesRequested
						)}
					/>
				</Row>
				<Row className="mt-2">
					<Col sm={12} lg={12} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Volume de atendimentos por dia da semana</Card.Title>
							</Card.Header>
							<Card.Body>
								<BarChart
									categories={dashboardData.aggregatedByDayOfWeek.map(
										(aggregated) => SHORT_DAYS_OF_WEEK[aggregated.key] || ""
									)}
									series={[
										{
											name: "Atendimentos",
											data: dashboardData.aggregatedByDayOfWeek.map(
												(aggregated) => aggregated.total
											),
										},
									]}
									colors={["#04e69a"]}
									minHeight={400}
									categoryTextWidth={400}
									baseMultiplier={10}
									isVertical
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<Row className="page-break-before">
					<Col sm={12} lg={12} className="mb-2">
						<Card className="shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Volume de atendimentos por hora do dia</Card.Title>
							</Card.Header>
							<Card.Body>
								<BarChart
									categories={dashboardData.aggregatedByHourOfDay.map(
										(aggregated) =>
											dayjs().hour(aggregated.key).minute(0).format("HH:mm")
									)}
									series={[
										{
											name: "Atendimentos",
											data: dashboardData.aggregatedByHourOfDay.map(
												(aggregated) => aggregated.total
											),
										},
									]}
									colors={["#04e69a"]}
									minHeight={400}
									categoryTextWidth={400}
									baseMultiplier={10}
									isVertical
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				<AttendanceByProfessionalModal
					show={showModal}
					onHide={() => setShowModal(false)}
					dashboardData={dashboardData}
				/>
				<Row className="page-break-before">
					<Col sm={12} lg={12} className="mb-2">
						<Card className="h-100 shadow-sm pb-4">
							<Card.Header className="d-flex justify-content-between fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Top profissionais com mais atendimentos</Card.Title>
								<Button
									className="gap-2 no-print"
									variant="secondary"
									onClick={() => setShowModal(true)}
								>
									<FaEye /> Detalhes
								</Button>
							</Card.Header>
							<Card.Body className="overflow-auto" style={{ maxHeight: 600 }}>
								<AttendanceByProfessionalTable
									aggregatedByProfessionalsAndMedicalAttendanceType={
										dashboardData.aggregatedByProfessionalsAndMedicalAttendanceType
									}
									averageTimeInSecondsByProfessional={
										dashboardData.averageTimeInSecondsByProfessional
									}
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</PrintComponent>
		</div>
	);
}
